import { confounderDurationSchema, confounderReasonSchema, connectionTypeSchema, connectionWhoSchema, healthBehaviourSchema, } from "@healthos/shared";
import { z } from "zod";
export const GetSignalsQuery = z.object({
    limit: z.string().optional(),
    // Without this the newest N rows are whatever kinds happen to be busiest,
    // so a once-a-day kind is crowded out by a wearable writing every minute.
    kind: z.string().min(1).max(80).optional(),
    // ISO instant. Pairs with kind to ask for one kind over one window.
    since: z.string().optional(),
    // The other end of that window. Without it a caller asking for today gets the
    // newest N rows from `since` onward, and a kind written ahead of time (a
    // calendar synced a week out) buries today's rows under next week's.
    until: z.string().optional(),
});
export const SignalItem = z.object({
    userId: z.string(),
    kind: z.string(),
    source: z.string(),
    payload: z.record(z.string(), z.unknown()),
    occurredAt: z.string(),
});
export const GetSignalsResponse = z.array(SignalItem);
export const GetSignalTimelineQuery = z.object({
    /** Max 720 (30 days) for manage-logged-signals; default timeline views use shorter windows. */
    hours: z.coerce.number().int().min(1).max(720).optional(),
    limit: z.coerce.number().int().min(1).max(300).optional(),
    until: z.string().datetime().optional(),
});
export const SignalTimelineCategory = z.enum([
    "sleep_recovery",
    "movement",
    "food",
    "calendar",
    "medication",
    "body_metrics",
    "stress_connection",
    "connection",
    "plan",
    "environment",
    "app_activity",
    "other",
]);
export const SignalTimelineBucket = z.enum(["morning", "afternoon", "evening", "night"]);
export const SignalTimelineItem = z.object({
    id: z.string(),
    kind: z.string(),
    signalKinds: z.array(z.string()).min(1),
    category: SignalTimelineCategory,
    categoryLabel: z.string(),
    title: z.string(),
    summary: z.string(),
    detailLabel: z.string().nullable(),
    sourceLabel: z.string(),
    /** Member-facing "logged from" badge (e.g. "From Slack check-in"), distinct
     * from `sourceLabel`'s device/service name. `null` when no override
     * applies — the UI falls back to its own default label. */
    sourceBadge: z.string().nullable(),
    occurredAt: z.string(),
    timeLabel: z.string(),
    count: z.number().int().positive(),
    manualEntry: z
        .object({
        id: z.string().uuid(),
        type: z.string().min(1),
        signalIds: z.array(z.number().int().positive()).min(1),
        editable: z.boolean(),
        payload: z.record(z.string(), z.unknown()),
        detailRows: z.array(z.string()).max(8),
    })
        .nullable()
        .optional(),
});
export const SignalTimelineActiveSource = z.object({
    source: z.string(),
    sourceLabel: z.string(),
    lastSyncedAt: z.string(),
    lastSyncedLabel: z.string(),
    signalCount: z.number().int().positive(),
    categories: z.array(SignalTimelineCategory),
});
export const SignalTimelineGroup = z.object({
    id: z.string(),
    bucket: SignalTimelineBucket,
    label: z.string(),
    dateLabel: z.string(),
    timeRangeLabel: z.string(),
    itemCount: z.number().int().nonnegative(),
    categories: z.array(SignalTimelineCategory),
    items: z.array(SignalTimelineItem),
});
export const GetSignalTimelineResponse = z.object({
    generatedAt: z.string(),
    timezone: z.string(),
    window: z.object({
        since: z.string(),
        until: z.string(),
        hours: z.number().int().positive(),
    }),
    activeSources: z.array(SignalTimelineActiveSource),
    groups: z.array(SignalTimelineGroup),
});
export const FeedbackSentiment = z.enum(["up", "down", "neutral"]);
export const FeedbackSubjectType = z.enum([
    "microstep",
    "article",
    "story",
    "recipe",
    "reset",
    "brief",
    "reminder",
    "plan_item",
    "grocery_item",
    "grocery_recipe",
    "attention_card",
    "library_item",
    "screen",
    "other",
]);
export const FeedbackSurface = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9_.:-]+$/);
export const SubmitFeedbackRequest = z.object({
    about: z.string().min(1).max(300),
    sentiment: FeedbackSentiment,
    text: z.string().max(1000).nullable().optional(),
    subjectId: z.string().min(1).max(200).nullable().optional(),
    subjectType: FeedbackSubjectType.nullable().optional(),
    surface: FeedbackSurface.nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const SubmitFeedbackResponse = z.object({ ok: z.boolean() });
const ManualEntryNote = z.string().max(1000).nullable().optional();
const ManualEntryLabel = z.string().min(1).max(120).optional();
const ManualEntryQuickAction = z.string().min(1).max(80).optional();
const ManualMediaBase64 = z
    .string()
    .min(1)
    .max(14_000_000)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, "must be base64 data without a data URL prefix");
export const ManualVoiceNoteMime = z.enum([
    "audio/m4a",
    "audio/mp4",
    "audio/mpeg",
    "audio/wav",
    "audio/webm",
]);
export const ManualFoodImageMime = z.enum(["image/jpeg", "image/png", "image/webp"]);
export const FoodPhotoAnalysis = z.object({
    score: z.number().int().min(0).max(100),
    label: z.enum(["nourishing", "balanced", "less_nourishing", "unclear"]),
    summary: z.string().min(1).max(240),
    detectedFoods: z.array(z.string().min(1).max(80)).max(6),
    confidence: z.enum(["low", "medium", "high"]),
});
export const TranscribeManualVoiceNoteRequest = z.object({
    mime: ManualVoiceNoteMime,
    dataBase64: ManualMediaBase64,
});
export const TranscribeManualVoiceNoteResponse = z.object({
    transcript: z.string().min(1).max(4_000),
    truncated: z.boolean(),
});
export const AnalyzeManualFoodPhotoRequest = z.object({
    mime: ManualFoodImageMime,
    dataBase64: ManualMediaBase64.max(7_000_000),
});
export const AnalyzeManualFoodPhotoResponse = FoodPhotoAnalysis;
const ManualEntryBase = z.object({
    occurredAt: z.string().datetime().optional(),
    note: ManualEntryNote,
    surface: FeedbackSurface.nullable().optional(),
    quickAction: ManualEntryQuickAction,
});
export const ManualFoodEntryRequest = ManualEntryBase.extend({
    type: z.literal("food"),
    meal: ManualEntryLabel,
    mealType: z.string().min(1).max(40).optional(),
    photoAnalysis: FoodPhotoAnalysis.optional(),
    calories: z.number().nonnegative().max(10_000).optional(),
    proteinG: z.number().nonnegative().max(500).optional(),
    carbsG: z.number().nonnegative().max(800).optional(),
    fatG: z.number().nonnegative().max(500).optional(),
    fiberG: z.number().nonnegative().max(200).optional(),
    sugarG: z.number().nonnegative().max(500).optional(),
    sodiumMg: z.number().nonnegative().max(50_000).optional(),
});
export const ManualHydrationEntryRequest = ManualEntryBase.extend({
    type: z.literal("hydration"),
    amountMl: z.number().int().positive().max(10_000),
    beverage: ManualEntryLabel,
});
export const ManualConnectionEntryRequest = ManualEntryBase.extend({
    type: z.literal("connection"),
    connectionType: connectionTypeSchema,
    who: connectionWhoSchema.optional(),
    rating: z.number().int().min(1).max(5),
});
export const ManualSleepKind = z.enum(["night_sleep", "nap", "quality_note"]);
export const ManualSleepEntryRequest = ManualEntryBase.extend({
    type: z.literal("sleep"),
    sleepKind: ManualSleepKind.default("quality_note"),
    sleepHours: z.number().positive().max(24).optional(),
    quality: z.enum(["poor", "ok", "good", "great", "restless"]).optional(),
    label: ManualEntryLabel,
});
export const ManualMoodEntryRequest = ManualEntryBase.extend({
    type: z.literal("mood"),
    label: ManualEntryLabel,
    voiceTranscript: z.string().min(1).max(4_000).optional(),
    mood: z.number().int().min(1).max(5).optional(),
    energy: z.number().int().min(1).max(5).optional(),
    stress: z.number().int().min(1).max(5).optional(),
    focus: z.number().int().min(1).max(5).optional(),
});
export const ManualSymptomsEntryRequest = ManualEntryBase.extend({
    type: z.literal("symptoms"),
    symptoms: z.array(z.string().min(1).max(80)).max(8).optional(),
    severity: z.number().int().min(1).max(10).optional(),
    label: ManualEntryLabel,
});
export const ManualIntakeType = z.enum([
    "coffee",
    "tea",
    "energy_drink",
    "caffeine",
    "alcohol",
    "none",
    "other",
]);
export const ManualCaffeineAlcoholEntryRequest = ManualEntryBase.extend({
    type: z.literal("caffeine"),
    intakeType: ManualIntakeType,
    amountLabel: ManualEntryLabel,
    caffeineMg: z.number().nonnegative().max(1_000).optional(),
    alcoholServings: z.number().nonnegative().max(20).optional(),
});
export const ManualWorkoutEntryRequest = ManualEntryBase.extend({
    type: z.literal("workout"),
    activity: z.string().min(1).max(80),
    durationMinutes: z
        .number()
        .positive()
        .max(24 * 60)
        .optional(),
    intensity: z.enum(["easy", "moderate", "hard"]).optional(),
    steps: z.number().int().nonnegative().max(200_000).optional(),
});
export const ManualMedicationStatus = z.enum(["taken", "skipped", "delayed", "moved", "note"]);
export const ManualMedicationEntryRequest = ManualEntryBase.extend({
    type: z.literal("medication"),
    medication: z.string().min(1).max(120),
    status: ManualMedicationStatus.default("taken"),
    doseAmount: z.number().positive().max(10_000).optional(),
    doseUnit: z.literal("mg").default("mg"),
    injectionSite: z.string().min(1).max(80).nullable().optional(),
    cadenceDays: z.number().min(1).max(90).optional(),
}).superRefine((value, ctx) => {
    if (value.status === "taken" && value.doseAmount === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["doseAmount"],
            message: "doseAmount is required when status is taken",
        });
    }
});
export const ManualVitalsMetric = z.enum([
    "weight",
    "blood_pressure",
    "glucose",
    "resting_hr",
    "hrv",
    "temperature",
    "hba1c",
]);
export const ManualVitalsEntryRequest = ManualEntryBase.extend({
    type: z.literal("vitals"),
    metric: ManualVitalsMetric,
    kg: z.number().positive().max(1_000).optional(),
    systolic: z.number().positive().max(350).optional(),
    diastolic: z.number().positive().max(250).optional(),
    glucoseMgDl: z.number().positive().max(2_000).optional(),
    bpm: z.number().positive().max(260).optional(),
    hrvMs: z.number().positive().max(1_000).optional(),
    temperatureC: z.number().min(25).max(45).optional(),
    hba1cPercent: z.number().min(3).max(20).optional(),
}).superRefine((value, ctx) => {
    const requiredByMetric = {
        weight: ["kg"],
        blood_pressure: ["systolic", "diastolic"],
        glucose: ["glucoseMgDl"],
        resting_hr: ["bpm"],
        hrv: ["hrvMs"],
        temperature: ["temperatureC"],
        hba1c: ["hba1cPercent"],
    };
    for (const field of requiredByMetric[value.metric]) {
        if (value[field] === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${field} is required for ${value.metric}`,
            });
        }
    }
});
// A confounder: the member acknowledging that a reading dipped for a reason
// outside their control. Carries no measurement and no free text. `duration`
// is a preset the server resolves to an effectiveThrough window; omit it to
// take the per-reason default. `cardFamily` is the behaviour of the card the
// member acknowledged (reactive path), used to resolve an "other" reason to a
// single family; absent on the proactive Add to Today path. affectedFamilies
// is never client-supplied: the server stamps it from the shared matrix.
export const ManualDisruptorEntryRequest = ManualEntryBase.extend({
    type: z.literal("disruptor"),
    reason: confounderReasonSchema,
    duration: confounderDurationSchema.optional(),
    cardFamily: healthBehaviourSchema.optional(),
    sourceSurface: z.enum(["today_card", "add_to_today"]).default("add_to_today"),
    decisionId: z.string().max(200).optional(),
    microstepId: z.string().max(200).optional(),
});
export const RecordManualSignalEntryRequest = z.union([
    ManualFoodEntryRequest,
    ManualHydrationEntryRequest,
    ManualConnectionEntryRequest,
    ManualSleepEntryRequest,
    ManualMoodEntryRequest,
    ManualSymptomsEntryRequest,
    ManualCaffeineAlcoholEntryRequest,
    ManualWorkoutEntryRequest,
    ManualMedicationEntryRequest,
    ManualVitalsEntryRequest,
    ManualDisruptorEntryRequest,
]);
export const CreatedManualSignalEntry = z.object({
    id: z.number().int().positive(),
    kind: z.string(),
    occurredAt: z.string(),
});
export const RecordManualSignalEntryResponse = z.object({
    manualEntryId: z.string().uuid(),
    entries: z.array(CreatedManualSignalEntry).min(1),
});
export const ManualSignalEntryParams = z.object({
    id: z.string().uuid(),
});
/**
 * `GET /api/signals/manual/:id`'s response: the one manual entry's raw
 * `type`/`payload`, resolved directly by `manualEntryId` rather than by
 * scanning a recent-signals window
 * (`SignalStore.listSelfReportManualEntry` + `resolveManualEntryItem`,
 * `signal-timeline-items.ts`). Exists specifically so an edit flow
 * (`useLogbookEntryEditing`'s `onEditEntry`, `use-logbook-tab-data.ts`) can
 * rehydrate its form without depending on the entry still being inside
 * `GET /timeline`'s count-limited window.
 */
export const GetManualSignalEntryResponse = z.object({
    manualEntryId: z.string().uuid(),
    type: z.string(),
    payload: z.record(z.string(), z.unknown()),
    occurredAt: z.string(),
    title: z.string(),
});
export const UpdateManualSignalEntryRequest = RecordManualSignalEntryRequest;
export const UpdateManualSignalEntryResponse = RecordManualSignalEntryResponse;
export const DeleteManualSignalEntryResponse = z.object({
    ok: z.boolean(),
    deletedCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=signals.js.map